import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ShoppingBag, X } from 'lucide-react';
import { previewPurchases, recentPurchases } from '../data/recentPurchases';
const INITIAL_DELAY_MS = 5000;
const VISIBLE_MS = 5000;
const GAP_MS = 4000;
export function PurchaseNotification() {
  const purchases = useMemo(() => recentPurchases.length > 0 ? recentPurchases : isEditorPreview() ? previewPurchases : [], []);
  const [index, setIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (isDismissed || purchases.length === 0) return;
    const delay = isVisible ? VISIBLE_MS : hasStarted ? GAP_MS : INITIAL_DELAY_MS;
    const timer = window.setTimeout(() => {
      if (isVisible) {
        setIsVisible(false);
        setIndex(current => (current + 1) % purchases.length);
      } else {
        setHasStarted(true);
        setIsVisible(true);
      }
    }, delay);
    return () => window.clearTimeout(timer);
  }, [isVisible, isDismissed, hasStarted, purchases.length]);
  if (isDismissed || purchases.length === 0) return null;
  const purchase = purchases[index];
  const minutesLabel = `${purchase.minutesAgo} minute${purchase.minutesAgo === 1 ? '' : 's'} ago`;
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[320px] max-w-[calc(100vw-2rem)] pointer-events-none" aria-live="polite" data-id="element-239" data-mp="19nbsroo2p7eh">
      <AnimatePresence data-id="element-240" data-mp="zsa4ev1mg9cy">
        {isVisible && <motion.div key={`${purchase.firstName}-${purchase.city}-${index}`} initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 16,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto relative flex items-center gap-3.5 bg-white rounded-2xl border border-blue-950/5 shadow-xl shadow-blue-950/20 py-3 pl-3 pr-9 overflow-hidden" role="status" data-id="element-241" data-mp="zpe6v7tormp1">
            <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-amber-500" aria-hidden="true" data-id="element-242" data-mp="2jneln4y6b94q" />

            <div className="relative shrink-0 w-11 h-11 rounded-xl bg-blue-950 text-amber-500 flex items-center justify-center" data-id="element-243" data-mp="3ge70jz9dev4x">
              <ShoppingBag size={19} strokeWidth={2.2} data-id="element-244" data-mp="3bfvqbnf5yim6" />
            </div>

            <div className="min-w-0 flex-1" data-id="element-245" data-mp="37c7kb966jv31">
              <p className="flex items-center gap-1.5 text-sm text-blue-950 leading-5 whitespace-nowrap" data-id="element-246" data-mp="37f3hgva7efqf">
                <img src={`https://flagcdn.com/${purchase.countryCode}.svg`} alt={`${purchase.country} flag`} className="w-[18px] h-[13px] rounded-[2px] object-cover shrink-0 ring-1 ring-black/5" data-id="element-247" data-mp="32g0dzf9vghkk" />
                <span className="font-bold" data-id="element-248" data-mp="6hzoihvlxuf4">{purchase.firstName}</span>
                <span className="text-blue-950/70 truncate" data-id="element-249" data-mp="bf9lqjp8nadi">
                  from {purchase.city}, {purchase.country}
                </span>
              </p>
              <p className="text-sm text-blue-950/70 leading-5 whitespace-nowrap" data-id="element-250" data-mp="ffju230i6cvj">
                purchased <span className="font-semibold text-blue-950" data-id="element-251" data-mp="ka7mgcymnrfe">Weightlifting Coach Ready</span>
              </p>
              <p className="flex items-center gap-1.5 text-xs text-slate-500 leading-4 mt-1" data-id="element-252" data-mp="p9k48jan68b4">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden="true" data-id="element-253" data-mp="u83u2uscw48r" />
                {minutesLabel}
              </p>
            </div>

            <button type="button" onClick={() => setIsDismissed(true)} className="absolute top-2 right-2 w-6 h-6 rounded-full text-slate-400 hover:text-blue-950 hover:bg-slate-50 flex items-center justify-center transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-700" aria-label="Close notification" data-id="element-254" data-mp="135nygclimgnl">
              <X size={13} data-id="element-255" data-mp="25omxviwsosx2" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function isEditorPreview(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}